import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getBookingBullionRequest,
  getBookingStatusHistoryRequest,
  getTransactionSummaryRequest,
  revokeBookingRequest,
  saveBookingBullionRequest,
  updateBookingStatusRequest,
} from '../../../redux/BookingBullion/bookingBullionSlice';
import TableCompo from '../Table/TableCompo';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import { getLiveRates } from '../../../redux/slices/metalRateSlice';
import { canShowPermission } from '../Permissions/permissionObject';
import FeatureUnavailable from '../../Comman/FeatureUnavailable';
import { getAppConfig } from '../../../redux/Configuration/configurationSlice';
import useCsvConverter from '../../../hooks/convertToCSV';
import { apiRequest } from '../../../redux/Reports/reportsSlice';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import { render } from '@testing-library/react';

const BookingBullion = () => {
  const dispatch = useDispatch();
  const { convertToCsv } = useCsvConverter();
  const { bookingList = [], bookingTotal = 0 } = useSelector(
    (state) => state.bookingBullion,
  );
  const liveRatesData = useSelector(
    (state) => state.metalRate?.liveRates || [],
  );
  const { transactionSummary } = useSelector((state) => state.bookingBullion);
  console.log('Transaction Summary ', transactionSummary);

  const metals = useSelector((state) =>
    Array.isArray(state.metals?.metals) ? state.metals.metals : [],
  );
  const decimal = useSelector(
    (state) => state?.appConfig?.configData?.decimalPrecision || 4,
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const { statusHistory = [], historyLoading } = useSelector(
    (state) => state.bookingBullion,
  );
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [editId, setEditId] = useState(null);
  const [selectedBullion, setSelectedBullion] = useState(null);
  const [actionRow, setActionRow] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const [form, setForm] = useState({
    amount: '',
    date: '',
    bullionId: '',
    description: '',
    rate: '',
  });

  useEffect(() => {
    dispatch(
      getBookingBullionRequest({
        page: pagination.page + 1,
        limit: pagination.limit,
        search,
      }),
    );
  }, [dispatch, pagination, search]);

  const handleTableChange = (action, tableState) => {
    switch (action) {
      case 'changePage':
        setPagination((prev) => ({ ...prev, page: tableState.page }));
        break;

      case 'changeRowsPerPage':
        setPagination({ page: 0, limit: tableState.rowsPerPage });
        break;

      case 'search':
        setSearch(tableState.searchText || '');
        setPagination((prev) => ({ ...prev, page: 0 }));
        break;

      default:
        break;
    }
  };

  const handleOpenHistory = (id) => {
    dispatch(getBookingStatusHistoryRequest(id));
    setHistoryOpen(true);
  };
  const getStatusColor = (status) => {
    if (status === 'PENDING') return '#f9a825';
    if (status === 'APPROVED') return '#2e7d32';
    if (status === 'REJECTED') return '#d32f2f';
    return '#999';
  };

  useEffect(() => {
    dispatch(getLiveRates());
    dispatch(getAppConfig());
  }, [dispatch]);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error('Error parsing stored permissions', error);
      }
    }
  }, []);
  const canCreateBookingBullion = canShowPermission(
    permissions,
    'bookingBullion',
    'create',
  );
  const canEditBookingBullion = canShowPermission(
    permissions,
    'bookingBullion',
    'edit',
  );
  const canEditJeweller = canShowPermission(
    permissions,
    'instalaxmiPermission',
    'view',
  );
  const canEdit = canShowPermission(permissions, 'jeweller', 'edit');

  useEffect(() => {
    if (!form.bullionId || !liveRatesData.length || !metals.length) return;

    const bullion = metals.find((item) => item._id === form.bullionId);

    if (!bullion) return;

    const metalName = bullion.metalName?.toLowerCase();
    const purity = Number(bullion.purity);

    let calculatedRate = 0;

    // GOLD
    if (metalName === 'gold') {
      const gold999 = liveRatesData.find(
        (item) => item.liveRateName === 'GOLD_999',
      );

      const gold995 = liveRatesData.find(
        (item) => item.liveRateName === 'GOLD_995',
      );

      const rate999 = Number(gold999?.ratePerGram || 0);

      const rate995 = Number(gold995?.ratePerGram || 0);

      if (purity === 999) {
        calculatedRate = rate999;
      } else if (purity === 995) {
        calculatedRate = rate995;
      } else {
        calculatedRate = (rate999 * purity) / 999;
      }
    }

    // SILVER
    if (metalName === 'silver') {
      const silver999 = liveRatesData.find(
        (item) => item.liveRateName === 'SILVER_999',
      );

      const baseSilverRate = Number(silver999?.ratePerGram || 0);

      if (purity === 999) {
        calculatedRate = baseSilverRate;
      } else {
        calculatedRate = (baseSilverRate * purity) / 999;
      }
    }

    setForm((prev) => ({
      ...prev,
      rate: Number(calculatedRate).toFixed(2),
    }));
  }, [form.bullionId, liveRatesData, metals]);
  const configData = useSelector((state) => state.appConfig.configData);

  const columns = [
    {
      field: 'jewellerName',
      headerName: 'Jeweller Name',
      render: (_, row) => row?.jewellerData?.jewellerName || '-',
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      render: (_, row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleString('en-IN') : '-',
    },
    {
      field: 'date',
      headerName: 'Booking Date',
      render: (_, row) => new Date(row.date).toLocaleDateString('en-IN'),
    },
    { field: 'amount', headerName: 'Amount', render: (_, row) => Number(row?.amount || 0).toFixed(2) },
    {
      field: 'weight',
      headerName: 'Weight',
      render: (_, row) => Number(row?.weight || 0).toFixed(decimal),
    },

    {
      field: 'bullion',
      headerName: 'Bullion',
      render: (_, row) => {
        const name = row?.bullionData?.metalName;
        const karat = row?.bullionData?.karat;
        const purity = row?.bullionData?.purity;

        return name ? `${name} (${karat || '-'}K - ${purity || '-'})` : '-';
      },
    },
    { field: 'description', headerName: 'Description' },
    {
      field: 'image',
      headerName: 'File',
      render: (_, row) => {
        const hasFile = row?.file && row?.file !== 'null' && row?.file !== '';

        return hasFile ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityIcon />}
            onClick={() => {
              setPreviewImage(row.file);
              setPreviewOpen(true);
            }}
          >
            View
          </Button>
        ) : (
          '-'
        );
      },
    },
    ...(canEditBookingBullion
      ? [
          {
            field: 'status',
            headerName: 'Status',
            render: (_, row) => {
              const status = row?.latestStatus?.status || 'PENDING';

              let bgColor = '#999';

              if (status === 'PENDING') bgColor = '#f9a825';
              if (status === 'APPROVED') bgColor = '#2e7d32';
              if (status === 'REJECTED') bgColor = '#d32f2f';

              // BOTH permissions → clickable
              if (canEditJeweller && canEditBookingBullion) {
                return (
                  <Button
                    variant="contained"
                    onClick={() => handleStatusClick(row)}
                    sx={{
                      backgroundColor: bgColor,
                      color: '#fff',
                      textTransform: 'none',
                      minWidth: '120px',
                      '&:hover': {
                        backgroundColor: bgColor,
                      },
                    }}
                  >
                    {status}
                  </Button>
                );
              }

              // ONLY bookingBullion edit permission → just show status
              return (
                <Chip
                  label={status}
                  sx={{
                    backgroundColor: bgColor,
                    color: '#fff',
                    fontWeight: 500,
                    minWidth: '120px',
                  }}
                />
              );
            },
          },
        ]
      : []),
    {
      field: 'history',
      headerName: 'History',
      render: (_, row) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleOpenHistory(row._id)}
        >
          History
        </Button>
      ),
    },
    ...(canEditBookingBullion && canEdit
      ? [
          {
            field: 'edit',
            headerName: 'Edit',
            render: (_, row) => (
              <IconButton onClick={() => handleEdit(row)}>
                <EditIcon />
              </IconButton>
            ),
          },
        ]
      : []),
    {
      field: 'createdBy',
      headerName: 'Created By',
      render: (_, row) => row?.createdByData?.name || '-',
    },
    {
      field: 'updatedBy',
      headerName: 'Updated By',
      render: (_, row) => row?.updatedByData?.name || '-',
    },
  ];

  const handleEdit = (row) => {
    setEditId(row._id);

    setForm({
      amount: row.amount || '',
      date: row.date ? row.date.split('T')[0] : '',
      bullionId: row?.bullionData?._id || '',
      description: row.description || '',
      rate: row.rate ? Number(row.rate).toFixed(2) : '',
      file: row.file || '',
      weight: row.weight || '',
    });
    setSelectedBullion(row?.bullionData || null);

    setFile(null);

    setOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let updatedForm = {
      ...form,
      [name]: value,
    };

    // amount -> weight auto calculate
    if (name === 'amount') {
      const amount = Number(value || 0);

      const rate = Number(form.rate || 0);

      if (rate > 0) {
        updatedForm.weight = (amount / rate).toFixed(decimal);
      }
    }

    setForm(updatedForm);
  };

  const handleSubmit = () => {
    if (!form.amount) return alert('Amount required');
    if (!form.date) return alert('Date required');
    if (!form.bullionId) return alert('Bullion required');
    if (!form.rate) return alert('Rate required');
    if (!form.weight) return alert('Weight required');
    let finalFile;

    if (file) {
      finalFile = file;
    } else if (editId) {
      finalFile = form.file;
    } else {
      finalFile = null;
    }

    const payload = {
      ...form,
      weight: Number(form.weight),
      file: finalFile,
      ...(editId && { id: editId }),
    };

    console.log('Payload ', payload);

    dispatch(saveBookingBullionRequest(payload));

    setOpen(false);
    setEditId(null);
    setForm({
      amount: '',
      date: '',
      bullionId: '',
      description: '',
      rate: '',
      weight: '',
    });
    setFile(null);
  };

  const handleStatusClick = (row) => {
    setActionRow(row);
  };

  const handleApprove = () => {
    dispatch(
      updateBookingStatusRequest({
        id: actionRow._id,
        status: 'APPROVED',
      }),
    );
    setActionRow(null);
  };

  const handleReject = () => {
    setRejectOpen(true);
  };

  const handleRejectSubmit = () => {
    if (!rejectReason) return alert('Enter reason');

    dispatch(
      updateBookingStatusRequest({
        id: actionRow._id,
        status: 'REJECTED',
        des: rejectReason,
      }),
    );

    setRejectOpen(false);
    setRejectReason('');
    setActionRow(null);
  };

  const handleRevoke = () => {
    if (!actionRow?._id) return;

    dispatch(revokeBookingRequest(actionRow._id));

    setActionRow(null);
  };
  useEffect(() => {
    if (!transactionSummary) return;

    setForm((prev) => ({
      ...prev,

      // amount editable
      amount: Number(transactionSummary?.totalAmount || 0).toFixed(2),

      // readonly display
      weight: Number(transactionSummary?.totalWeight || 0).toFixed(decimal),
    }));
  }, [transactionSummary]);

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: 'text/csv' });

    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');

    a.href = url;

    a.download = filename;

    a.click();

    window.URL.revokeObjectURL(url);
  };

  const handleDownloadBookingCsv = () => {
    dispatch(
      apiRequest({
        method: 'GET',
        endpoint: '/api/v1/booking-bullion/bookingBullionCsv',
        query: {},
        callback: (response) => {
          const csv = convertToCsv(response?.data || []);

          triggerCsvDownload(csv, 'booking-bullion-request.csv');
        },
      }),
    );
  };
  if (configData?.featureToggle?.Booking_Bullion !== true) {
    return <FeatureUnavailable heading="Booking Request" />;
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Box
        sx={{
          background: '#fff',
          borderRadius: '16px',
          mt: { xs: 10, md: 10 },
          mb: { xs: 2, md: 2 },
          width: '100%',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        {/* Header */}
        <Box display="flex" justifyContent="space-between" p={2}>
          <Typography fontSize={22} fontWeight={700}>
            Booking Bullion
          </Typography>

          {canCreateBookingBullion && (
            <Button
              variant="contained"
              onClick={() => {
                setEditId(null);

                setForm({
                  amount: '',
                  date: '',
                  bullionId: '',
                  description: '',
                  rate: '',
                  weight: '',
                });

                setFile(null);

                setOpen(true);
              }}
            >
              Create Record
            </Button>
          )}
        </Box>

        {/* Table */}
        <CardContent>
          <TableCompo
            columns={columns}
            data={bookingList}
            totalCount={bookingTotal}
            onTableChange={handleTableChange}
            page={pagination.page}
            limit={pagination.limit}
            extraButtons={
              <Button
                size="sm"
                variant="outlined"
                onClick={handleDownloadBookingCsv}
              >
                Download CSV
              </Button>
            }
          />
        </CardContent>
      </Box>

      {/* Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>{editId ? 'Edit Booking' : 'Create Booking'}</DialogTitle>
        <DialogContent>
          <TextField
            type="date"
            name="date"
            fullWidth
            margin="normal"
            value={form.date}
            onChange={(e) => {
              handleChange(e);

              const selectedDate = e.target.value;
              if (selectedDate && form.bullionId) {
                dispatch(
                  getTransactionSummaryRequest({
                    date: selectedDate,
                    bullionId: form.bullionId,
                  }),
                );
              }
            }}
          />

          <TextField
            select
            label="Bullion"
            name="bullionId"
            fullWidth
            margin="normal"
            value={form.bullionId}
            onChange={(e) => {
              handleChange(e);

              const selectedBullionId = e.target.value;
              if (form.date && selectedBullionId) {
                dispatch(
                  getTransactionSummaryRequest({
                    date: form.date,
                    bullionId: selectedBullionId,
                  }),
                );
              }
            }}
          >
            {metals.map((metal) => (
              <MenuItem key={metal._id} value={metal._id}>
                {metal.metalName} - {metal.purity}-{metal.karat}K
              </MenuItem>
            ))}
          </TextField>
          {/* <TextField
            label="Rate"
            name="rate"
            fullWidth
            margin="normal"
            value={form.rate}
            InputProps={{
              readOnly: true,
            }}
          /> */}

          <TextField
            label="Amount"
            name="amount"
            type="number"
            fullWidth
            margin="normal"
            value={form.amount}
            onChange={handleChange}
            InputProps={{
              readOnly: !editId,
            }}
            inputProps={{
              min: 0,
            }}
          />

          <TextField
            label="Weight"
            name="weight"
            type="number"
            fullWidth
            margin="normal"
            value={form.weight}
            InputProps={{
              readOnly: true,
            }}
            inputProps={{
              min: 0,
            }}
          />

          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={3}
            margin="normal"
            value={form.description}
            onChange={handleChange}
          />

          {/* File Upload */}
          <Box mt={2}>
            <Button variant="outlined" component="label">
              Upload File
              <input
                hidden
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
              />
            </Button>

            {file && <Typography mt={1}>{file.name}</Typography>}
          </Box>
          {editId && !file && form?.file && (
            <Box mt={2}>
              <img
                src={form.file}
                alt="uploaded"
                style={{
                  width: '100%',
                  maxHeight: '150px',
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 3 }}
            onClick={handleSubmit}
          >
            Save
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Status History</DialogTitle>

        <DialogContent>
          {historyLoading ? (
            <Typography>Loading...</Typography>
          ) : statusHistory.length === 0 ? (
            <Typography>No history found</Typography>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 2,
                boxShadow: 3,
              }}
            >
              <Table>
                {/* Header */}
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>
                      <b>Status</b>
                    </TableCell>
                    <TableCell>
                      <b>Description</b>
                    </TableCell>
                    <TableCell>
                      <b>Updated By</b>
                    </TableCell>
                    <TableCell>
                      <b>Date & Time</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                {/* Body */}
                <TableBody>
                  {statusHistory.map((item, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: '#fafafa',
                        },
                      }}
                    >
                      {/* Status */}
                      <TableCell>
                        <Chip
                          label={item.status}
                          sx={{
                            backgroundColor: getStatusColor(item.status),
                            color: '#fff',
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>

                      {/* Description */}
                      <TableCell>{item.des || '-'}</TableCell>

                      {/* Updated By */}
                      <TableCell>{item.updatedBy?.name || '-'}</TableCell>

                      {/* Date */}
                      <TableCell>
                        {new Date(item.updatedAt).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={!!actionRow}
        onClose={() => setActionRow(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Update Status</DialogTitle>

        <DialogContent>
          {/* ✅ STATUS TABLE (NEW) */}
          {actionRow?.status?.length ? (
            <TableContainer
              component={Paper}
              sx={{ borderRadius: 2, boxShadow: 3 }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>
                      <b>Status</b>
                    </TableCell>
                    <TableCell>
                      <b>Description</b>
                    </TableCell>
                    <TableCell>
                      <b>Updated By</b>
                    </TableCell>
                    <TableCell>
                      <b>Date & Time</b>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {actionRow.status.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Chip
                          label={item.status}
                          sx={{
                            backgroundColor: getStatusColor(item.status),
                            color: '#fff',
                          }}
                        />
                      </TableCell>

                      <TableCell>{item.des || '-'}</TableCell>

                      <TableCell>
                        {item.updatedBy?.name || item.updatedBy || '-'}
                      </TableCell>

                      <TableCell>
                        {new Date(item.updatedAt).toLocaleString('en-IN')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography>No status found</Typography>
          )}

          {actionRow?.latestStatus?.status === 'PENDING' ? (
            <Box display="flex" gap={2} mt={3}>
              <Button
                variant="contained"
                color="success"
                onClick={handleApprove}
              >
                Approve
              </Button>

              <Button variant="contained" color="error" onClick={handleReject}>
                Reject
              </Button>
            </Box>
          ) : (
            <Box mt={3}>
              <Button
                variant="contained"
                color="warning"
                onClick={handleRevoke}
              >
                Revoke
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)}>
        <DialogTitle>Reject Reason</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason"
          />

          <Button
            variant="contained"
            sx={{ mt: 2 }}
            fullWidth
            onClick={handleRejectSubmit}
          >
            Submit
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <Box
          sx={{
            position: 'relative',
            background: '#000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: '#b61313',
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          <img
            src={previewImage}
            alt="preview"
            style={{
              width: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Dialog>
    </Box>
  );
};

export default BookingBullion;
